import { type FC, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCreateStreamEntrypointMutation } from "@/partner/entities/streamEntrypoints";

interface CreateStreamEntrypointDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

// Один шаг: имя точки — по образцу CreateStreamDialog.tsx (streams-фича),
// без даты начала (у точки входа её нет). После создания родитель
// открывает диалог самой точки (см. StreamEntrypoints.tsx).
export const CreateStreamEntrypointDialog: FC<
  CreateStreamEntrypointDialogProps
> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState("");

  const [createStreamEntrypoint, { loading }] =
    useCreateStreamEntrypointMutation({
      refetchQueries: ["StreamEntrypointsList"],
      onCompleted: (data) => {
        enqueueSnackbar(t("streamEntrypoints.created"), {
          variant: "success",
        });
        setTitle("");
        onCreated(data.createStreamEntrypoint.id);
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    });

  const handleSubmit = async () => {
    if (!title.trim()) return;
    await createStreamEntrypoint({ variables: { input: { title } } });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("streamEntrypoints.createTitle")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label={t("streamEntrypoints.titleField")}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !title.trim()}
        >
          {t("common.actions.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
